import { z } from "zod";

/**
 * Contrat des routes posees par la couche Lab (`src/lab/`). Il vit a cote du contrat
 * de l'app pour la meme raison : le backend valide ce qu'il repond, le frontend rejoue
 * le schema a la reception, et une rupture casse la compilation des deux cotes.
 */

export const legalPageIdSchema = z.enum(["mentions", "confidentialite", "cgu", "cgv"]);

export const legalSectionSchema = z.object({
  heading: z.string(),
  paragraphs: z.array(z.string()),
});

export const legalDocumentSchema = z.object({
  id: legalPageIdSchema,
  path: z.string(),
  title: z.string(),
  updatedAt: z.string(),
  sections: z.array(legalSectionSchema),
  missing: z.array(z.string()),
});
export type LegalDocumentPayload = z.infer<typeof legalDocumentSchema>;

export const legalIndexSchema = z.object({
  documents: z.array(legalDocumentSchema),
});

/** Compteurs d'une periode (le jour courant, ou le mois courant), en memoire du processus. */
export const metricsPeriodSchema = z.object({
  /** `AAAA-MM-JJ` pour le jour, `AAAA-MM` pour le mois, en temps universel. */
  period: z.string(),
  events: z.record(z.string(), z.number()),
  spend: z.object({
    allowed: z.number(),
    denied: z.number(),
    costCents: z.number(),
  }),
  /** Evenements qui ne sont pas arrives au Lab : refuses avant envoi, ou par lab-tools. */
  lost: z.object({
    invalid: z.number(),
    rejected: z.number(),
  }),
});

/** Plafond tel que lab-tools le rend : seuil d'alerte et coupure sont decides par lui. */
export const budgetSchema = z.object({
  configured: z.boolean(),
  period: z.string(),
  limitCents: z.number(),
  spentCents: z.number(),
  remainingCents: z.number(),
  pct: z.number(),
  warn: z.boolean(),
  exhausted: z.boolean(),
});

export const metricsSchema = z.object({
  app: z.string(),
  version: z.string(),
  generatedAt: z.string(),
  /** Rappel en clair : ces compteurs sont un point de controle, pas la source de verite. */
  source: z.string(),
  day: metricsPeriodSchema,
  month: metricsPeriodSchema,
  guard: z.object({
    /** false = lab-tools n'a pas repondu depuis le dernier essai. */
    reachable: z.boolean(),
    /** true = l'etat connu a plus de 30 s. */
    stale: z.boolean(),
    flags: z.object({
      stopAll: z.boolean(),
      pauseSpend: z.boolean(),
      pausePublish: z.boolean(),
      paused: z.boolean(),
      spendPaused: z.boolean(),
      signupOpen: z.boolean(),
      maintenance: z.boolean(),
    }),
    serving: z.object({
      mode: z.enum(["normal", "maintenance", "paused"]),
      signupOpen: z.boolean(),
    }),
    /** Derniere raison de depense rendue par lab-tools, `null` tant qu'il n'a rien dit. */
    spendReason: z.string().nullable(),
    /** Plafonds tels que lab-tools les rend : celui de l'app, et l'agrege du Lab. */
    appBudget: budgetSchema.nullable(),
    labBudget: budgetSchema.nullable(),
  }),
  queue: z.object({
    pending: z.number(),
    dropped: z.number(),
    failures: z.number(),
    rejected: z.number(),
  }),
  legal: z.object({
    missing: z.array(z.string()),
  }),
});
export type MetricsPayload = z.infer<typeof metricsSchema>;

/** Reponse de `GET /api/me/export` : tout ce que l'app detient sur une personne. */
export const personalDataExportSchema = z.object({
  subject: z.object({ id: z.string() }),
  exportedAt: z.string(),
  retention: z.object({
    accountDays: z.number(),
    analyticsDays: z.number(),
    logDays: z.number(),
    attributionDays: z.number(),
  }),
  /** Une entree par fournisseur de donnees enregistre par l'app ou par le template. */
  data: z.record(z.string(), z.unknown()),
});
export type PersonalDataExport = z.infer<typeof personalDataExportSchema>;

/** Reponse de `DELETE /api/me` : ce qui a ete efface, et par qui. */
export const personalDataErasureSchema = z.object({
  subject: z.object({ id: z.string() }),
  erasedAt: z.string(),
  erased: z.array(z.string()),
});
export type PersonalDataErasure = z.infer<typeof personalDataErasureSchema>;

/*
 * Paiement (option `billing`). Les schemas sont declares dans tous les rendus, comme les
 * pages legales : le contrat ne depend pas de l'option, seul le montage des routes en
 * depend. Un fichier identique partout, c'est un conflit de moins a la propagation.
 */

export const checkoutRequestSchema = z.object({
  /** Cle du produit au catalogue de `app.config.ts`. Absente = le produit par defaut. */
  product: z.string().min(1).max(60).optional(),
  /** Adresse prefaite sur la page Stripe. Utile quand l'app n'a pas de comptes. */
  email: z.email().optional(),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/** Reponse de `POST /api/billing/checkout` : l'adresse de la page de paiement Stripe. */
export const checkoutSessionSchema = z.object({
  url: z.string(),
  product: z.string(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string(),
});
export type CheckoutSession = z.infer<typeof checkoutSessionSchema>;

/** Reponse de `POST /api/billing/portal` : l'adresse du portail client Stripe. */
export const portalSessionSchema = z.object({ url: z.string() });

export const entitlementSchema = z.object({
  product: z.string(),
  status: z.string(),
  /** Fin de la periode payee pour un abonnement, `null` pour un achat unique. */
  currentPeriodEnd: z.string().nullable(),
});

/** Reponse de `GET /api/billing/entitlements` : a quoi la personne connectee a droit. */
export const entitlementsSchema = z.object({
  subject: z.object({ id: z.string() }),
  entitlements: z.array(entitlementSchema),
});
export type Entitlements = z.infer<typeof entitlementsSchema>;
